/*
 * Contact email service. Handles the required contact email update after a direct credentials login
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2026 Gallagher Bassett Services, Inc.
 *
 */

import { Injectable } from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import {
  Observable,
  throwError
} from 'rxjs';
import {
  catchError,
  map
} from 'rxjs/operators';
import { Constants } from 'app/shared/config/constants';

export interface PendingContactEmailUpdate {
  userId: string;
  redirectContext: any;
  // Set once the new email is saved, so a reload still shows the success step and its Continue
  updatedEmail?: string;
}

@Injectable ( {
  providedIn: 'root',
} )
export class ContactEmailService {
  private readonly url = Constants.usersServiceUrl;
  // Bypasses the interceptors, which would replace the Authorization header with the stored Reporter token
  private readonly http: HttpClient;

  constructor ( httpBackend: HttpBackend ) {
    this.http = new HttpClient ( httpBackend );
  }

  public isInvalid ( email: string | null | undefined ): boolean {
    return email?.trim ()
      .toLowerCase () === Constants.invalidContactEmail;
  }

  public get isUpdateRequired (): boolean {
    return localStorage.getItem ( Constants.localStorageKeys.reporterContactEmailUpdateRequired ) === 'true';
  }

  // A hand-off login has no Reporter session, so it must not block the Reporter routes of whoever else is logged in
  public requireUpdate ( userId: string, redirectContext: any ): void {
    if ( !redirectContext.handOffToken ) {
      localStorage.setItem ( Constants.localStorageKeys.reporterContactEmailUpdateRequired, 'true' );
    }
    sessionStorage.setItem ( Constants.contactEmailPendingRedirectStorageKey, JSON.stringify ( { userId, redirectContext } ) );
  }

  public get pendingUpdate (): PendingContactEmailUpdate | null {
    try {
      return JSON.parse ( sessionStorage.getItem ( Constants.contactEmailPendingRedirectStorageKey ) );
    } catch ( e ) {
      return null;
    }
  }

  public get isHandOff (): boolean {
    return !!this.pendingUpdate?.redirectContext?.handOffToken;
  }

  // Clears everything. Used when the Reporter session ends
  public clear (): void {
    localStorage.removeItem ( Constants.localStorageKeys.reporterContactEmailUpdateRequired );
    sessionStorage.removeItem ( Constants.contactEmailPendingRedirectStorageKey );
  }

  // The email is saved, so a Reporter login is no longer blocked. The update of this tab is kept until the user continues
  public markUpdated ( userId: string, email: string, redirectContext: any ): void {
    if ( !redirectContext?.handOffToken ) {
      localStorage.removeItem ( Constants.localStorageKeys.reporterContactEmailUpdateRequired );
    }
    sessionStorage.setItem ( Constants.contactEmailPendingRedirectStorageKey,
      JSON.stringify ( { userId, redirectContext, updatedEmail: email } ) );
  }

  // Clears only the update of this tab. The Reporter flag is shared between tabs and may belong to another session by now
  public clearPending (): void {
    sessionStorage.removeItem ( Constants.contactEmailPendingRedirectStorageKey );
  }

  public fetchEmail ( userId: string, accessToken: string ): Observable<string> {
    return this.http.get<{ email?: string }> ( this.userUrl ( userId ), { headers: this.headers ( accessToken ) } )
      .pipe ( map ( user => user?.email ) );
  }

  public sendCode ( userId: string, email: string ): Observable<string> {
    return this.http.post ( `${ this.userUrl ( userId ) }/verification-code/send`, null, {
      headers: this.headers ( this.currentAccessToken ),
      params: new HttpParams ().set ( 'email', email ),
      responseType: 'text',
    } )
      .pipe ( catchError ( error => throwError ( this.withParsedErrorBody ( error ) ) ) );
  }

  // Verifies the code and updates the email in one call. A used code is consumed even when the update itself fails
  public resetEmail ( userId: string, email: string, code: string ): Observable<void> {
    return this.http.post<void> ( `${ this.userUrl ( userId ) }/email-reset`, { email }, {
      headers: this.headers ( this.currentAccessToken ),
      params: new HttpParams ().set ( 'verificationCode', code ),
    } );
  }

  // The send response is plain text, so Angular leaves the JSON error body of a failed response as a string
  private withParsedErrorBody ( error: HttpErrorResponse ): HttpErrorResponse {
    if ( typeof error.error !== 'string' ) {
      return error;
    }
    try {
      return new HttpErrorResponse ( {
        error: JSON.parse ( error.error ),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url,
      } );
    } catch ( e ) {
      return error;
    }
  }

  private get currentAccessToken (): string {
    return this.pendingUpdate?.redirectContext?.handOffToken ??
      localStorage.getItem ( Constants.localStorageKeys.reporterAccessToken );
  }

  private userUrl ( userId: string ): string {
    return `${ this.url }/${ encodeURIComponent ( userId ) }`;
  }

  private headers ( accessToken: string ): HttpHeaders {
    return new HttpHeaders ( { Authorization: `Bearer ${ accessToken }` } );
  }
}
